<template lang="html">
  <div :class="[keepFocusClass, fillRemainingClass, noBordersClass]" class="rounded-sm" ref="parlay-textbox-and-button">
    <div ref="parlay-textbox-container" :class="[fillRemainingClass, minHeightClass]" class="relative" :style="{ maxHeight: `${maxHeight}px` }">
      <textarea
        data-gramm="false"
        ref="parlay-textbox"
        @input="updateMessage"
        @blur="$emit('blur')"
        @keydown="newline"
        :disabled="disabled"
        :style="textAreaStyle"
        :placeholder="placeholder"
        :value="value"
        :maxLength="maxLength"
        rows="6"
        class="rounded border-2 border-slate-300 placeholder-slate-400"
      />
      <div class="invisible-text">{{ ' ' + value + ' ' }}</div>
      <div v-show="allowEmoji">
        <emoji-picker class="emoji-container" v-if="allowEmoji && chatIsEnabled" @emoji="append" :search="search">
          <div class="emoji-invoker" slot="emoji-invoker" slot-scope="{ events: { click: clickEvent } }" @click.stop="clickEvent">
            <svg
              height="24"
              viewBox="0 0 24 24"
              width="24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0 0h24v24H0z" fill="none" />
              <path
                d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"
              />
            </svg>
          </div>
          <div slot="emoji-picker" slot-scope="{ emojis, insert, display }">
            <div
              class="emoji-picker"
              :style="{ top: display.y + 'px', left: display.x + 'px' }"
            >
              <div class="emoji-picker__search">
                <input type="text" v-model="search" v-focus />
              </div>
              <div>
                <div v-for="(emojiGroup, category) in emojis" :key="category">
                  <h5>{{ category }}</h5>
                  <div class="emojis">
                    <span
                      v-for="(emoji, emojiName) in emojiGroup"
                      :key="emojiName"
                      @click="insert(emoji)"
                      :title="emojiName"
                      >{{ emoji }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </emoji-picker>
        <div v-else class="emoji-container emoji-invoker-blocked">
          <svg
            height="24"
            viewBox="0 0 24 24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M0 0h24v24H0z" fill="none" />
            <path
              d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"
            />
          </svg>
        </div>
      </div>
    </div>
    <div
      @click="focusTextarea"
      v-if="showSubmitButton"
      class="text-box-button-row end-xs items-center row no-margin no-padding"
    >
      <!-- If there's no message, it acts as a placeholder to push button to the right -->
      <span class="pl-caption italics col-xs-7 start-xs">
        {{ submittingMessage }}</span
      >
      <div class="flex-container-end">
        <div v-if="savedChanges">
          <pl-button
            ref="textbox-button"
            :disabled="true"
            v-if="autoSave"
            @click.native.stop="sendMessage(text)"
          >
            Saved
          </pl-button>
        </div>
        <div>
          <pl-button
            ref="textbox-button"
            @click.native.stop="sendMessage(text)"
            :class="[theme]"
          >
            {{ buttonTitle }}
          </pl-button>
        </div>
      </div>
    </div>
    <!-- slot for any kind of message e.g in discussLDTeacher
    (can use postion absolute and relative to place message) -->
    <slot></slot>
  </div>
</template>
<script>
  import Vue from 'vue';
  import { EventBus } from 'utils/event-bus';
  import EmojiPicker from 'vue-emoji-picker';

  Vue.directive("clickOutside", {
      bind: function (el, binding, vnode) {
        el.clickOutsideEvent = function (event) {
          if (!(el == event.target || el.contains(event.target)))
            vnode.context[binding.expression](event);
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unbind: function (el) {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    });

  export default Vue.extend({
    data() {
      return {
        text: '',
        savedChanges: false,
        search: '',
        display: {
          x: 0,
          y: 0,
          visible: false,
        },
      };
    },
    components: {
      EmojiPicker,
    },
    computed: {
      themeClass() {
        return { [this.theme]: true };
      },
      noBordersClass() {
        return { 'no-borders': this.noBorders };
      },
      keepFocusClass() {
        return { 'parlay-textbox-and-button-focused': this.keepFocus };
      },
      fillRemainingClass() {
        return { 'flex-column-container': this.fillRemaining };
      },
    },
    props: {
      fillRemaining: {
        type: Boolean,
        default: false,
      },
      buttonTitle: {
        type: String,
        default: 'Send',
      },
      value: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: 'Anything to add?',
      },
      submittingMessage: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      textAreaStyle: {
        type: String,
        default: '',
      },
      theme: {
        type: String,
        default: 'pl-primary',
      },
      maxHeight: {
        type: Number,
        default: null,
      },
      keepFocus: {
        type: Boolean,
        default: false,
      },
      showSubmitButton: {
        type: Boolean,
        default: true,
      },
      noBorders: {
        type: Boolean,
        default: false,
      },
      focusOnMounted: {
        type: Boolean,
        default: false,
      },
      minHeightClass: {
        type: String,
        default: 'h-28',
      },
      autoSave: {
        type: Boolean,
        default: false,
      },
      enableShiftEnter: {
        type: Boolean,
        default: false,
      },
      maxLength: {
        type: Number,
        default: null,
      },
      allowEmoji: {
        type: Boolean,
        default: false,
      },
      chatIsEnabled: {
        type: Boolean,
        default: false,
      },
    },
    directives: {
      focus: {
        inserted(el) {
          el.focus();
        },
      },
    },
    methods: {
      append(emoji) {
        this.$refs['parlay-textbox'].value += emoji;
        this.updateMessage();
      },
      focusTextarea() {
        this.$refs['parlay-textbox'].focus();
      },
      updateMessage() {
        this.$emit('input', this.$refs['parlay-textbox'].value);
        this.savedChanges = false;
        if (this.autoSave) {
          this.$emit('saveChanges');
        }
      },
      sendMessage(text) {
        this.$emit('messageSent', text);
        this.$refs['textbox-button'].$el.blur();
      },
      newline(e) {
        if (this.enableShiftEnter && e.keyCode === 13 && !e.shiftKey) {
          e.preventDefault();
          this.$emit('enter', e);
        }
      },
    },
    mounted() {
      if (this.focusOnMounted) {
        setTimeout(() => {
          this.focusTextarea();
        });
      }
      EventBus.$on('savedEvaluation', () => {
        this.savedChanges = true;
      });
    },
  });
</script>
<style scoped lang="stylus">
  .parlay-textbox-and-button
    border-radius: 5px;
    border: 2px solid #E1E8EE;
    background-color: white;
  .flex-container-end
    padding: 0;
    margin: 0;
    display: flex;
    justify-content: flex-end;
  .no-borders
    border: none;
  .feedback-theme
    border-radius: 5px;
    border: 2px solid #5fa95c;
    background-color: #fff;
  .parlay-textbox-and-button-focused,
  .parlay-textbox-and-button:focus-within
    border-radius: 5px;
    &.pl-primary
      border: 2px solid pl-primary;
    &.pl-secondary
      border: 2px solid pl-secondary;
    &.pl-slate
      border: 2px solid pl-slate;
    &.muted-focus
      border: 2px solid #E1E8EE;
    &.no-borders
      border: none;
  .parlay-textbox-container
    position: relative;
  .emoji-invoker {
    border-radius: 50%;
    cursor: pointer;
    position: relative;
  }
  .emoji-container
    position: absolute
    right: 5px
    bottom: 15px
  .emoji-invoker > svg {
    fill: #b1c6d0;
  }
  .emoji-invoker-blocked
    border-radius: 50%;
    cursor: cursor;
  .emoji-invoker-blocked > svg {
    fill: #b1c6d0;
  }
  .emoji-picker {
    z-index: 1;
    border: 1px solid #ccc;
    width: 15rem;
    height: 20rem;
    overflow: scroll;
    padding: 1rem;
    box-sizing: border-box;
    border-radius: 0.5rem;
    background: #fff;
    box-shadow: 1px 1px 8px #c7dbe6;
    margin-left: 2.5rem;
  }
  .emoji-picker__search {
    display: flex;
  }
  .emoji-picker__search > input {
    flex: 1;
    border-radius: 10rem;
    border: 1px solid #ccc;
    padding: 0.5rem 1rem;
    outline: none;
  }
  .emoji-picker h5 {
    margin-bottom: 0;
    color: #b1b1b1;
    text-transform: uppercase;
    font-size: 0.8rem;
    cursor: default;
  }
  .emoji-picker .emojis {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
  .emoji-picker .emojis:after {
    content: "";
    flex: auto;
  }
  .emoji-picker .emojis span {
    padding: 0.2rem;
    cursor: pointer;
    border-radius: 5px;
  }
  .emoji-picker .emojis span:hover {
    background: #ececec;
    cursor: pointer;
  }
  .invisible-text
    -webkit-transition: min-height 1s; /* Safari */
    transition: min-height 1s;
    padding: 15px;
    visibility: hidden;
    min-height: 0px;
    overflow-y: hidden;
    /*
    Pre-wrap: preserve spacing and newlines, but wrap text.
    Pre: preserve spacing and newlines but don't wrap text.
    "pre" does not wrap well on Firefox, even with word-wrap:break-word.
    "pre" on Chrome works with word-wrap, but exhibits different behavior:
    Instead of entire words being moved to the next line for wrapping,
    the browser will cut words in the middle for wrapping.
    "pre-line" has Firefox issues
    */
    white-space: pre-wrap;
    /* Required for wrapping lines in Webkit,
    but not necessary in Firefox if you have white-space wrapping
    (pre-wrap, normal, pre-line) already set */
    word-wrap: break-word;
    overflow-wrap: break-word;
  .text-box-button-row
    border-top: 1px solid pl-fog;
    max-height: 50px;
    /* focus within does not work in edge or IE */
    /* transition: max-height 1s;
    max-height: 0px;
    overflow-y: hidden; */
  .parlay-textbox-and-button-focused,
  .parlay-textbox-and-button:focus-within
    .text-box-button-row
    /* border-top: 1px solid pl-fog; */
      max-height: 50px;
  textarea
    padding: 10px;
    position: absolute;
    left: 0px;
    height: 100%;
    resize: none;
    width: 100%;
    border: none;
    outline: none;
    font-family: inherit;
  textarea:hover
    text-shadow: none;
</style>


// WEBPACK FOOTER //
// textbox.vue?2c4ecd82